<script lang="ts" setup>
  import { useAuthStore, WelfarePlan } from '@tundr/auth';
  import { BreadCrumb } from '@tundr/breadcrumb';
  import {
    Button,
    ButtonColor,
    ButtonVariant,
    FontIcon,
  } from '@tundr/ds-components';
  import {
    handleHttpErrors,
    showValidationErrorNotification,
  } from '@tundr/http';
  import { useLocale } from '@tundr/i18n';
  import {
    Notification,
    NotificationVariant,
    useNotification,
  } from '@tundr/notification';
  import { RoutingView } from '@tundr/routing';
  import { useServiceWorkerUpdate } from '@tundr/service-worker';
  import {
    Sidebar,
    SidebarNav,
    SidebarVariant,
    type SidebarItemProps,
  } from '@tundr/sidebar';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { useToast } from '@tundr/toast';
  import { useWizardStore, WizardSidebar } from '@tundr/wizard-stepper';
  import { computed, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useGetHrVerificationStatus } from '@core/api/accounts-memberships/accounts-memberships.api';
  import { useBankProviderLogin } from '@core/api/bank-provider/bank-provider.api';
  import { useGetFringeCreditTypes } from '@core/api/credit-types/credit-types.api.ts';
  import { ENV_VARS } from '@core/config/env';
  import { appRoutesDictionary } from '@core/router/app-routes.dictionary.ts';
  import { useCreditTypesStore } from '@core/store/credit-types/credit-types.store.ts';
  import { observeWindow } from '@core/utils/bank-provider/bank-provider-consent-manager';
  import SidebarCompanyRow from '@shared/components/Company/SidebarCompanyRow/SidebarCompanyRow.vue';
  import UserMenu from '@shared/components/UserMenu/UserMenu.vue';
  import { useSupportChat } from '@app/useSupportChat';
  import {
    contentBackgroundClass,
    contentHostClass,
    contentInsetClass,
    contentMainClass,
    contentWithNotificationClass,
    headerWrapperClass,
    layoutClass,
    sidebarHeaderSectionClass,
    wizardCancelClass,
    wizardContentHostClass,
    wizardRailClass,
    wizardUserMenuClass,
  } from './AppRouter.css';

  const notification = useNotification();
  const { showSuccess, showError } = useToast();

  const route = useRoute();
  const router = useRouter();

  const authStore = useAuthStore();
  const creditTypesStore = useCreditTypesStore();
  const wizardStore = useWizardStore();

  const activeWizardNamespace = computed(() => {
    const namespace = route.meta.wizardNamespace as string | undefined;
    return namespace && wizardStore.steppers[namespace] ? namespace : undefined;
  });

  const contentInsetClasses = computed(() => [
    contentInsetClass,
    {
      [contentWithNotificationClass]: Boolean(
        notification.getLastNotification.value,
      ),
    },
  ]);

  const contentHostClasses = computed(() => [
    contentHostClass,
    { [wizardContentHostClass]: Boolean(activeWizardNamespace.value) },
  ]);

  const onCancelWizard = () => {
    const exitRouteName = route.meta.wizardExitRoute as string | undefined;
    if (exitRouteName) {
      router.push({ name: exitRouteName });
    }
  };

  const isStandardPlan = computed(() => {
    return authStore.currentWelfarePlan === WelfarePlan.STANDARD;
  });

  const isCompletePlan = computed(() => {
    return authStore.currentWelfarePlan === WelfarePlan.COMPLETE;
  });

  const HR_VERIFICATION_NOTIFICATION_KEY = 'hr-verification';

  const timeoutInstance = ref<ReturnType<typeof setTimeout> | null>(null);

  const { t } = useLocale('common');

  const { openSupportChat } = useSupportChat();

  const { run: runBankProviderLogin } = useBankProviderLogin({
    onSuccess: async (res) => {
      const observeWindowConfigured = observeWindow(timeoutInstance);
      await observeWindowConfigured(res.data.loginUri);
      showSuccess({
        message: t('hr:hr_verification_status.success.title'),
        description: t('hr:hr_verification_status.success.description'),
      });
      setTimeout(() => {
        refreshGetHrStatus();
      }, 5000);
    },
    onError: () => {
      showError({
        message: t('hr:hr_verification_status.error.title'),
        description: t('hr:hr_verification_status.error.description'),
      });
    },
  });

  const { refresh: refreshGetHrStatus } = useGetHrVerificationStatus({
    onSuccess: (res) => {
      if (res.data?.requiresIdentityVerification) {
        notification.addNotification({
          key: HR_VERIFICATION_NOTIFICATION_KEY,
          title: t('hr:hr_verification_status.title'),
          message: t('hr:hr_verification_status.message'),
          variant: NotificationVariant.WARNING,
          dismissable: false,
          actions: [
            {
              label: t('hr:hr_verification_status.actions.confirm_permission'),
              onClick: () => {
                runBankProviderLogin({ redirectUri: window.location.href });
              },
            },
          ],
        });
      } else {
        notification.removeNotification(HR_VERIFICATION_NOTIFICATION_KEY);
      }
    },
  });

  useGetFringeCreditTypes({
    onSuccess: (response) => {
      if (response.data) {
        creditTypesStore.setCreditTypes(response.data.creditTypes);
      }
    },
    onError: (error) =>
      handleHttpErrors(
        { error, t, notifyFn: showError },
        showValidationErrorNotification('errors'),
      ),
  });

  const onGoToFAQs = () => {
    window.open(ENV_VARS.faqs, '_blank');
  };

  const { needRefresh, applyUpdate } = useServiceWorkerUpdate();

  const items = computed<SidebarItemProps[]>(() => [
    {
      label: t('pages.dashboard'),
      icon: TUNDR_ICONS.DASHBOARD,
      testId: 'dashboard',
      isHidden: !isStandardPlan.value,
      subItems: [
        {
          label: t('pages.dashboard'),
          to: appRoutesDictionary.dashboard.root,
          testId: 'dashboard',
        },
      ],
    },
    {
      label: t('pages.employees'),
      icon: TUNDR_ICONS.PEOPLE,
      subItems: [
        {
          label: t('pages.all_employees'),
          to: appRoutesDictionary.employees.root,
          testId: 'employees',
        },
        {
          label: t('pages.subscriptions'),
          to: appRoutesDictionary.subscriptions.root,
          testId: 'subscriptions',
        },
        {
          label: t('pages.card'),
          to: appRoutesDictionary.card.cardList,
          testId: 'card_list',
        },
      ],
    },
    {
      label: t('pages.company_management'),
      icon: TUNDR_ICONS.WRENCH,
      subItems: [
        {
          label: t('pages.hr'),
          to: appRoutesDictionary.hr.root,
          testId: 'hr',
        },
        {
          label: t('pages.offices'),
          to: appRoutesDictionary.offices.root,
          testId: 'offices',
        },
      ],
    },
    {
      label: t('pages.welfare_management'),
      icon: TUNDR_ICONS.COINS,
      subItems: [
        {
          label: t('pages.card_orders'),
          to: appRoutesDictionary.card.cardOrderslist,
          testId: 'card_orders',
        },
        {
          label: t('pages.credits'),
          to: appRoutesDictionary.credits.root,
          isHidden: !isStandardPlan.value,
          testId: 'credits',
        },
        {
          label: t('pages.flex_credits'),
          to: appRoutesDictionary.flexCredits.root,
          isHidden: !isCompletePlan.value,
          testId: 'flex_credits',
        },
        {
          label: t('pages.meal_vouchers'),
          to: appRoutesDictionary.mealVouchers.cardOrdersList,
          isHidden: !authStore.hasMealVoucher,
          testId: 'meal_vouchers',
        },
        {
          label: t('pages.meal_voucher_orders'),
          to: appRoutesDictionary.mealVouchers.ordersList,
          isHidden: !authStore.hasMealVoucher,
          testId: 'meal_voucher_orders',
        },
        {
          label: t('pages.fringe_thresholds'),
          to: appRoutesDictionary.fringeThresholds.root,
          isHidden: !isCompletePlan.value,
          testId: 'fringe_thresholds',
        },
        {
          label: t('pages.deposits_requests'),
          to: appRoutesDictionary.deposit.root,
          isHidden: !isCompletePlan.value,
          testId: 'deposits_requests',
        },
      ],
    },
    {
      label: t('pages.report'),
      icon: TUNDR_ICONS.FILE,
      isHidden: !isCompletePlan.value,
      subItems: [
        {
          label: t('pages.tracciato_busta_paga_reports'),
          to: appRoutesDictionary.tracciatoBustaPagaReports.root,
          testId: 'tracciato_busta_paga_reports',
        },
        {
          label: t('pages.benefit_reports'),
          to: appRoutesDictionary.voucherReports.root,
          testId: 'benefit_reports',
        },
      ],
    },
    {
      label: t('pages.support'),
      icon: TUNDR_ICONS.CHAT,
      subItems: [
        {
          label: t('pages.chat'),
          onClick: openSupportChat,
          testId: 'chat',
        },
        {
          label: t('pages.faq'),
          onClick: onGoToFAQs,
          onMiddleClick: onGoToFAQs,
          testId: 'faq',
        },
      ],
    },
  ]);
</script>

<template>
  <div :class="layoutClass">
    <div v-if="activeWizardNamespace" :class="wizardRailClass">
      <WizardSidebar :namespace="activeWizardNamespace">
        <template #header>
          <div :class="wizardUserMenuClass">
            <UserMenu :collapsed="false" :variant="SidebarVariant.OnBrand" />
          </div>
        </template>
        <template #bottom-cta>
          <div :class="wizardCancelClass">
            <Button
              :variant="ButtonVariant.Secondary"
              :color="ButtonColor.OnBrand"
              data-testid="wizard-cancel-button"
              is-ghost
              @click="onCancelWizard"
            >
              <FontIcon :icon-name="TUNDR_ICONS.CLOSE" />
              {{ t('actions.cancel') }}
            </Button>
          </div>
        </template>
      </WizardSidebar>
    </div>
    <Sidebar
      v-else
      :variant="SidebarVariant.Light"
      :update-available="needRefresh"
      @update-click="applyUpdate"
    >
      <template #header="{ collapsed }">
        <div :class="sidebarHeaderSectionClass">
          <UserMenu :collapsed="collapsed" />
          <SidebarCompanyRow :collapsed="collapsed" />
        </div>
      </template>
      <template #body>
        <SidebarNav :items="items" />
      </template>
    </Sidebar>
    <div :class="contentBackgroundClass">
      <div :class="contentInsetClasses">
        <Notification />
        <div :class="contentMainClass">
          <div :class="headerWrapperClass">
            <BreadCrumb />
          </div>
          <div :class="contentHostClasses">
            <RoutingView />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
